/**
 * friendlyErrorParsing — pure, deterministic parsing/translation logic for the
 * e-invoicing event drawer. NO React / Fluent UI imports live here so the logic is
 * unit-testable under the repo's CRA/Jest setup (which cannot transform Fluent UI's
 * ESM packages). The React rendering lives in FriendlyEventError.tsx.
 *
 * ============================================================================
 * PLAYBOOK: onboarding a NEW COUNTRY / PROVIDER CONNECTION
 * ============================================================================
 * All the translation catalogs live in THIS file. Pick the extension point by how
 * the provider reports errors (check a handful of real failed transactions first):
 *
 *  1. Error carries a MACHINE CODE (e.g. CFDI40158, FAT-1201, SdI "00305")
 *       → add `'CODE': 'English explanation'` to `ProviderErrorCatalog`.
 *       → if the code pattern is new, add a branch to `extractProviderCode()`.
 *         Keep it collision-free: for bare numeric codes, only return ones that
 *         actually exist in the catalog (see the SdI branch as the template).
 *
 *  2. Error is PLAIN LOCALIZED TEXT with no code (e.g. "Documento duplicado")
 *       → add `'normalized phrase': 'English'` to `ProviderPhraseCatalog`.
 *         `normalizePhrase()` lower-cases + strips accents, so key on the
 *         accent-free, lower-cased reason. High-confidence phrases only.
 *
 *  3. Error is an EN16931 / Peppol RULE (BR-*, PEPPOL-*)
 *       → usually already covered by `BusinessRuleFriendlyNames` +
 *         `getRuleExplanation()` prefix fallbacks. Add a prefix branch only for
 *         a bespoke national rule namespace.
 *
 *  Optional:
 *   - New success/sent status word from the connector → add to `StatusMeta`
 *     (the pill color + label) and a matching icon in FriendlyEventError.tsx.
 *   - New field that should dedupe across different codes (like postal/zip)
 *     → add a concept to `FIELD_CONCEPTS`.
 *
 *  GOLDEN RULE: translate off the CODE whenever one exists (language-independent);
 *  fall back to the curated PHRASE catalog only for code-less localized text.
 *  Never do fuzzy/free-text similarity — every mapping must be deterministic.
 *
 *  VERIFY (from src/einvoicing.frontend):
 *    npx tsc --noEmit
 *    $env:CI='true'; npx react-scripts test --watchAll=false --testPathPattern=friendlyErrorParsing
 *  Add cases to friendlyErrorParsing.test.ts for the new country before shipping.
 * ============================================================================
 */
import { ErrorCodeandSubErrorCodeFriendlyNameMapping } from '../../utils/FriendlyNameMappings';

// Plain-language explanations for the business/compliance rule codes that show up in
// supplier validation rejections across ALL countries. The core EN16931 / Peppol BIS
// rules (BR-*, BR-CO-*) are shared by every EU Peppol country (DK, NO, SE, FI, DE, IT,
// BE, LU, LV, IS, FR, ...). National schemes (Saudi ZATCA, India, Italy SdI) and VAT
// category rules are covered by prefix fallbacks in getRuleExplanation().
const BusinessRuleFriendlyNames: { [key: string]: string } = {
    // --- Mandatory content (EN16931 BR-01..BR-17) ---
    'BR-01': 'The invoice must reference a specification identifier (the format/standard it follows).',
    'BR-02': 'The invoice must have an invoice number.',
    'BR-03': 'The invoice must have an issue date.',
    'BR-04': 'The invoice must have a valid invoice type code.',
    'BR-05': 'The invoice must have a currency code.',
    'BR-06': 'The invoice must contain the seller name.',
    'BR-07': 'The invoice must contain the buyer name.',
    'BR-08': 'The invoice must contain the seller postal address.',
    'BR-09': 'The seller postal address must contain a country code.',
    'BR-10': 'The invoice must contain the buyer postal address.',
    'BR-11': 'The buyer postal address must contain a country code.',
    'BR-12': 'The invoice must have a sum of line net amounts.',
    'BR-13': 'The invoice must have a total amount without VAT.',
    'BR-14': 'The invoice must have a total amount with VAT.',
    'BR-15': 'The invoice must have an amount due for payment.',
    'BR-16': 'The invoice must have at least one invoice line.',
    'BR-17': 'A payee name is required when the payee differs from the seller.',
    // --- Calculation / totals (EN16931 BR-CO-*) — the most common rejections ---
    'BR-CO-03': 'The VAT point date and its date code cannot both be present.',
    'BR-CO-09': 'Each VAT identifier must start with the correct country prefix.',
    'BR-CO-10': 'The sum of all line net amounts must equal the invoice line total.',
    'BR-CO-11': 'The document-level allowances total must equal the sum of the individual allowances.',
    'BR-CO-12': 'The document-level charges total must equal the sum of the individual charges.',
    'BR-CO-13': 'The total without VAT must equal the line total, plus charges, minus allowances.',
    'BR-CO-14': 'The total VAT amount must equal the sum of the VAT amounts for each tax category.',
    'BR-CO-15': 'The total amount with VAT must equal the total without VAT plus the total VAT.',
    'BR-CO-16': 'The amount due for payment must equal the total with VAT, minus any prepaid amount, plus rounding.',
    'BR-CO-17': 'A VAT category amount must equal its taxable amount multiplied by the VAT rate.',
    'BR-CO-18': 'The invoice must have at least one VAT breakdown group.',
    'BR-CO-25': 'A payment due date or payment terms must be provided when an amount is due.',
    // --- Line level ---
    'BR-21': 'Each invoice line must have an identifier.',
    'BR-22': 'Each invoice line must have a quantity.',
    'BR-23': 'Each invoice line must have a unit of measure.',
    'BR-24': 'Each invoice line must have a net amount.',
    'BR-25': 'Each invoice line must have the item name.',
    'BR-26': 'Each invoice line must have a net price.',
    'BR-FR-09': 'The buyer SIRET (BT-46) must be valid and consistent with the buyer SIREN (BT-47).',
    'BR-FR-11': 'The buyer legal registration identifier (BT-47, SIREN) is required for this B2B transaction.',
    'BR-FR-12': 'The buyer electronic invoicing address (BT-49, EndpointID) is required; it is not an email address.',
    'BR-FR-21': 'The buyer electronic invoicing address (BT-49) must be present when required and begin with the buyer SIREN.',
    'BR-FR-23': 'The buyer electronic invoicing address (BT-49) must use the permitted identifier format, not an email address.',
    'BR-FREXT-CO-04': 'Each invoice line (BG-25) must have an invoiced item VAT category code (BT-151).',
    'UBL-FREXT-SR-48': 'Invoice line VAT-category cardinality is invalid; check the requirement for exactly one classified tax category in the supplier diagnostic.',
};

// Prefix-based fallbacks so unknown national/category rules still get a useful hint,
// covering every country rather than an explicit code list.
export function getRuleExplanation(ruleCode: string, fallback: string): string {
    if (!ruleCode) return fallback;
    if (BusinessRuleFriendlyNames[ruleCode]) return BusinessRuleFriendlyNames[ruleCode];
    if (/^BR-S(-|$)/.test(ruleCode)) return 'Standard-rated VAT rule: the taxable amount and VAT for the standard rate must be consistent.';
    if (/^BR-Z(-|$)/.test(ruleCode)) return 'Zero-rated VAT rule: zero-rated lines must have a 0% rate and no VAT amount.';
    if (/^BR-E(-|$)/.test(ruleCode)) return 'VAT-exempt rule: exempt lines require an exemption reason and no VAT amount.';
    if (/^BR-AE(-|$)/.test(ruleCode)) return 'Reverse-charge rule (VAT paid by the buyer): requires an exemption reason and no VAT amount.';
    if (/^BR-G(-|$)/.test(ruleCode)) return 'Export-outside-EU rule: requires an exemption reason and no VAT amount.';
    if (/^BR-IC(-|$)/.test(ruleCode)) return 'Intra-community supply rule: requires an exemption reason and the VAT numbers of both parties.';
    if (/^BR-O(-|$)/.test(ruleCode)) return 'Not-subject-to-VAT rule: cannot be combined with other VAT categories.';
    if (/^BR-IG(-|$)/.test(ruleCode)) return 'IGIC (Canary Islands) VAT category rule.';
    if (/^BR-IP(-|$)/.test(ruleCode)) return 'IPSI (Ceuta/Melilla) VAT category rule.';
    if (/^BR-KSA/.test(ruleCode)) return 'Saudi Arabia (ZATCA) national e-invoicing rule — see technical details for the exact requirement.';
    if (/^BR-(DE|IT|FR|NL|NO|SE|DK|FI|BE)/.test(ruleCode)) return 'Country-specific national rule — see technical details for the exact requirement.';
    if (/^PEPPOL/.test(ruleCode)) return 'Peppol BIS business rule — see technical details for the exact requirement.';
    return fallback;
}

// Fix mojibake produced by charset mismatches (e.g. guillemets « » arriving as U+FFFD).
export function fixEncoding(text: string): string {
    if (!text) return text;
    return text.replace(/\uFFFD/g, '"');
}

// Cleans the free-text "Additional details" clause into a readable sentence:
// collapse whitespace, strip leading punctuation, capitalise, end with a period.
function prettifyDetails(text: string): string {
    let t = (text || '').replace(/\s+/g, ' ').trim().replace(/^[:.\-\s]+/, '').trim();
    if (!t) return '';
    t = t.charAt(0).toUpperCase() + t.slice(1);
    if (!/[.!?]$/.test(t)) t += '.';
    return t;
}

// Strips provider "noise" that some connectors (e.g. GoSocket) wrap around the real
// reason: a leading "<X> error -" prefix, "CODE|" prefixes like "CFDI40158|", a
// trailing "- Messages: ..." echo, and an immediately duplicated sentence. Leaves the
// human-readable core reason (in whatever language the provider returned it).
function cleanProviderNoise(text: string): string {
    let t = (text || '').trim();
    // Drop a trailing "- Messages: ...." echo block.
    t = t.replace(/\s*[-–]\s*Messages?\s*:.*$/is, '').trim();
    // Drop a leading "<something> error -/:" prefix (e.g. "GoSocket error - ").
    t = t.replace(/^[A-Za-z][\w .]*?error\s*[-–:]\s*/i, '').trim();
    // Drop provider "CODE|" prefixes like "CFDI40158|" / "FAT-1201|".
    t = t.replace(/\b(CFDI\d{3,6}|CRP\d+|FAT-?\d+)\s*\|\s*/gi, '').trim();
    // Collapse a sentence that is immediately repeated ("X. X." or "X X").
    const half = Math.floor(t.length / 2);
    if (half > 8) {
        const a = t.slice(0, half).replace(/[\s.,;]+$/, '').trim();
        const b = t.slice(half).replace(/^[\s.,;]+/, '').trim();
        if (a && a.toLowerCase() === b.toLowerCase()) t = a;
    }
    return t;
}

// Provider / tax-authority error-code catalog → canonical English. This is the
// deterministic translation layer: the provider's localized message (Spanish, French,
// ...) is just a rendering of one of these codes, so we key off the CODE, never the
// free text. Seed with authoritative codes; populate the rest from each authority's
// official code list (SAT Anexo 20 for Mexico, Chorus Pro / PPF for France, SdI for
// Italy, ...). Verified entries carry a source comment.
const ProviderErrorCatalog: { [code: string]: string } = {
    // ---- Mexico — SAT CFDI 4.0 (source: SAT Anexo 20 v4.0 validation matrix) ----
    'CFDI40120': 'The place of issue (LugarExpedicion) is required and is missing.',
    'CFDI40134': "The receiver node is missing the required Name (Nombre) attribute.",
    'CFDI40135': "The receiver node is missing the required tax-address ZIP code (DomicilioFiscalReceptor).",
    'CFDI40136': "The receiver node is missing the required tax regime (RegimenFiscalReceptor).",
    'CFDI40142': "The issuer's RFC is not on SAT's list of registered, non-cancelled taxpayers.",
    'CFDI40143': "The issuer node is missing the required Name (Nombre) attribute.",
    'CFDI40147': "The receiver's tax address (DomicilioFiscalReceptor) must match an RFC that is registered and active in SAT's records.",
    'CFDI40157': "The receiver's RFC is not on SAT's list of registered, non-cancelled taxpayers.",
    'CFDI40158': "The receiver's tax regime (RegimenFiscalReceptor) must match the recipient's person type — individual (física) or legal entity (moral).",
    'CFDI40159': "The receiver's tax regime (RegimenFiscalReceptor) is not valid for the recipient.",
    'CFDI40160': "The CFDI use (UsoCFDI) does not correspond to the receiver's tax regime.",
    'CFDI40161': "The CFDI use (UsoCFDI) must match the recipient's person type (física/moral) and tax regime per the c_UsoCFDI catalog.",
    'CFDI40201': 'The subtotal amount is incorrect (line totals do not add up).',
    'CFDI40301': 'The issue date cannot be more than 72 hours before the stamping (timbre) date and time.',
    'CFDI40302': 'The issue date is outside the allowed range (later than the current date).',

    // ---- France — Chorus Pro / PPF platform file error (source: AIFE spec) ----
    'FAT-1201': 'Invalid or missing file format — the invoice file does not match an accepted format (Factur-X, UBL or CII).',

    // ---- Italy — SdI / FatturaPA rejection codes (source: fatturapa.gov.it, Agenzia delle Entrate) ----
    '00001': 'Invalid file name — it must follow the official pattern (country code + unique id + "_" + progressive, .xml or .xml.p7m) and must not duplicate a previously sent name.',
    '00200': 'The file does not conform to the FatturaPA format (fails the official XSD schema or is missing required fields).',
    '00300': 'The VAT number (IdFiscaleIVA) is not valid.',
    '00301': "The sender's tax code (Codice Fiscale) is not valid.",
    '00305': "The customer's VAT number (IdFiscaleIVA) is not valid — it must be exactly 11 digits.",
    '00306': "The customer's tax code (Codice Fiscale) is not valid — it must be 16 characters with no stray spaces.",
    '00311': 'The recipient code (Codice Destinatario) is not valid — verify it against the IPA index or with the recipient.',
    '00320': 'Error in the signing representative data.',
    '00400': 'Error in the document (invoice header) data.',
    '00401': 'Error in the VAT summary (riepiloghi IVA) data.',
    '00404': 'Error in the payment data.',
    '00409': 'Mismatch between the taxable amount (imponibile) and the VAT rate (aliquota).',
    '00411': 'Error in the CIG or CUP data (public-tender references).',
    '00417': 'Error in the "Causale" (reason) field.',
    '00418': 'Invalid document date ("Data" field).',
    '00419': 'Invalid document number ("Numero" field).',
    '00420': 'Error in the goods/services line data.',
    '00421': 'Error in the regulatory references.',
    '00422': 'Error in the stamp-duty (Imposta di bollo) indication.',
    '00423': 'Incorrect value in the "Total document amount" (Importo totale documento) field.',
    '00427': 'The recipient code (Codice Destinatario) has the wrong length (7 chars for B2B, 6 for public administration).',
    '00428': 'Missing CUP/CIG code where it is mandatory.',
    '00430': "The transmitter's tax code (Codice Fiscale trasmittente) is not valid.",
    '00437': "The recipient's certified email (PEC) is not valid.",
    '00444': 'The VAT nature code (Natura N2/N3/N6) is a deprecated generic value — use the specific sub-code (e.g. N2.1, N3.2, N6.1).',
};

// Deterministic PHRASE catalog for localized provider reasons that arrive WITHOUT a
// machine code (e.g. GoSocket / Edicom free text like "Documento duplicado"). Keyed on
// a normalized form of the reason (lower-case, accent-stripped, punctuation-trimmed) so
// the same reason always maps to the same English text. Only high-confidence, unambiguous
// phrases belong here — extend it as new recurring reasons are observed in the data.
const ProviderPhraseCatalog: { [normalized: string]: string } = {
    // ---- Mexico — GoSocket / SAT plain-text notifications ----
    'documento duplicado': 'Duplicate document — a document with the same details was already submitted to SAT.',
    'comprobante duplicado': 'Duplicate document — a document with the same details was already submitted to SAT.',
    'el comprobante ya fue timbrado': 'This document was already stamped (timbrado) — it cannot be submitted again.',
    'rfc del receptor no valido': "The receiver's RFC is not valid.",
    'rfc no valido': 'The RFC is not valid.',
    'sello mal formado o invalido': 'The digital seal (sello) is malformed or invalid.',
    // ---- France — Chorus Pro / PPF plain-text reasons ----
    'facture deja transmise': 'This invoice has already been submitted.',
    'format de fichier invalide ou manquant': 'Invalid or missing file format.',
    // ---- Italy — SdI plain-text reasons ----
    'documento gia inviato': 'This document has already been sent.',
};

// Normalizes a localized reason for phrase-catalog lookup: strips accents/diacritics,
// lower-cases, collapses whitespace, and trims surrounding punctuation.
export function normalizePhrase(text: string): string {
    return (text || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')  // drop combining diacritics (á→a, í→i, ç→c...)
        .toLowerCase()
        .replace(/[^a-z0-9 ]+/g, ' ')     // punctuation → space
        .replace(/\s+/g, ' ')
        .trim();
}

// Deterministically translates a localized reason to English via the phrase catalog.
// Returns '' when the reason is not (yet) catalogued.
function translatePhrase(text: string): string {
    return ProviderPhraseCatalog[normalizePhrase(text)] || '';
}

// Extracts a provider/tax-authority code from a (possibly localized) message.
// Deliberately narrow and deterministic so it does not collide with EN16931 rule
// codes (BR-*) or the Microsoft internal errorCode. For the numeric Italian SdI
// family it only returns a match that is actually catalogued, which rules out
// false positives from arbitrary numbers in the text.
export function extractProviderCode(message: string): string {
    // Mexico SAT CFDI 4.0
    const cfdi = message.match(/\b(CFDI\d{4,6})\b/i);
    if (cfdi) return cfdi[1].toUpperCase();
    // France Chorus Pro / PPF platform file error
    const fat = message.match(/\b(FAT-?\d{3,4})\b/i);
    if (fat) return fat[1].toUpperCase().replace(/^FAT(?=\d)/, 'FAT-');
    // Italy SdI (zero-padded 5-digit); accept only known codes to stay collision-free
    const sdiMatches = message.match(/\b0\d{4}\b/g);
    if (sdiMatches) {
        const known = sdiMatches.find((c) => c in ProviderErrorCatalog);
        if (known) return known;
    }
    return '';
}

// Where a translated title came from (for an accurate "translated from …" note).
export type TranslationSource = '' | 'code' | 'phrase';

export interface ParsedError {
    errorCode: string;
    subErrorCode: string;
    source: string;        // e.g. "EDICOM France"
    ruleCode: string;      // e.g. "BR-CO-14"
    providerCode: string;  // e.g. "CFDI40147" (tax-authority / supplier code)
    translated: boolean;   // title was resolved from a code/phrase catalog (guaranteed English)
    translationSource: TranslationSource;
    title: string;         // friendly one-line summary
    plainMessage: string;  // human sentence
    businessRule: string;  // friendly rule explanation
    additionalDetails: string; // the prettified "Additional details ..." clause, if any
    location: string;      // XPath / field location the error was reported at
    rawMessage: string;    // full original message (encoding-fixed)
}

// Markers that identify the technical "Original: ..." validator tail (EU standard validator
// dump, xpath, mapping token, or an EN16931/national rule code). Used to decide whether an
// "Original:" occurrence in a details clause is validator noise (strip) or genuine text (keep).
const TECHNICAL_TAIL_MARKERS = /(EU standard validator|validator map|\bMAP\s*=|\[\s*\/|\bBR-[A-Z]{1,3}(?:-[A-Z0-9]+)+\b|\bBR-\d{2}\b|\bEN16931)/i;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseSingle(raw: any): ParsedError {
    const errorCode = String(raw.errorCode ?? '').trim();
    const subErrorCode = String(raw.subErrorCode ?? '').trim();
    const rawMessage = fixEncoding(String(raw.errorMessage ?? ''));

    let working = rawMessage;

    // Pull the free-text "Additional details ..." clause: for wrapper errors like
    // "Unable to complete request" (1400-4) this carries the real, specific reason.
    let additionalDetails = '';
    const addlMatch = rawMessage.match(/\bAdditional\s+details\b\s*:?\s*(.+)$/is);
    if (addlMatch) {
        // "Additional details: ... Original: ..." — strip the technical "Original:" validator
        // tail so it doesn't leak into the user-facing details. Only strip when the specific
        // "Original:" segment looks technical (validator map / xpath / rule code); a provider
        // that uses the word "Original:" inside a genuine reason keeps its text intact — even
        // when a later, separate "Original:" technical tail follows it.
        let detailsText = addlMatch[1];
        const originalRe = /\bOriginal:/gi;
        const originalIdxs: number[] = [];
        let om: RegExpExecArray | null;
        while ((om = originalRe.exec(detailsText)) !== null) {
            originalIdxs.push(om.index);
        }
        for (let i = 0; i < originalIdxs.length; i++) {
            const start = originalIdxs[i];
            const end = i + 1 < originalIdxs.length ? originalIdxs[i + 1] : detailsText.length;
            // Test only this occurrence's own segment (up to the next "Original:"), so a
            // legitimate earlier reason isn't dropped because of a later technical tail.
            if (TECHNICAL_TAIL_MARKERS.test(detailsText.slice(start, end))) {
                detailsText = detailsText.slice(0, start);
                break;
            }
        }
        additionalDetails = prettifyDetails(cleanProviderNoise(detailsText));
        // Keep it out of the derived "plain" sentence so it isn't duplicated as the title.
        working = working.slice(0, addlMatch.index).trim();
    }

    // Cut the technical "Original:" tail (validator internals) out of the human portion.
    const originalIdx = working.search(/\bOriginal:/i);
    if (originalIdx >= 0) {
        working = working.slice(0, originalIdx);
    }

    // Capture the business "Rule: ..." clause if present.
    let businessRule = '';
    const ruleMatch = working.match(/\bRule:\s*([\s\S]+)$/i);
    if (ruleMatch) {
        businessRule = ruleMatch[1].trim().replace(/[.;\s]+$/, '');
        working = working.slice(0, ruleMatch.index).trim();
    }

    // Rule code, e.g. [BR-CO-14], [PEPPOL-EN16931-R040], [BR-KSA-79]. Prefer a bracketed
    // token; fall back to a standalone token anywhere in the message for suppliers that
    // don't use brackets.
    const bracketRule = rawMessage.match(/\[([A-Z]{2,}(?:-[A-Z0-9]+)+)\]/);
    const looseRule = rawMessage.match(/\b(PEPPOL-EN16931-R\d+|BR-(?:[A-Z]+-)*\d+|UBL-FREXT-SR-\d+)(?=\b|_BT)/i);
    const ruleCode = (bracketRule ? bracketRule[1] : (looseRule ? looseRule[1] : '')).toUpperCase();

    // Source / supplier prefix: the leading label before the first bracket or colon,
    // if it's short, single-line and not an xpath (works for "EDICOM France",
    // "Pagero Sweden", "ZATCA", "GoSocket India", ...).
    let source = '';
    const bracketPos = rawMessage.indexOf('[');
    const colonPos = rawMessage.indexOf(':');
    const positions = [bracketPos, colonPos].filter((p) => p >= 0);
    if (positions.length > 0) {
        const cut = Math.min(...positions);
        const candidate = rawMessage.slice(0, cut).trim();
        if (candidate && candidate.length <= 40 && !candidate.startsWith('/') && !candidate.includes('.')) {
            source = candidate.replace(/[:\-\s]+$/, '');
        }
    }

    // Strip codes, xpaths and the source prefix to leave a clean human sentence.
    let plain = working
        .replace(/\bat\s*\[[^\]]*\]\s*:?/gi, ' ')   // "at [/ubl:Invoice/...]:"
        .replace(/\[[^\]]*\]/g, ' ')                 // remaining [ ... ] tokens
        .replace(/\([A-Z0-9_]+\)/g, ' ')             // (REJ_SEMAN)
        .replace(/\s{2,}/g, ' ')
        .trim();
    if (source && plain.startsWith(source)) {
        plain = plain.slice(source.length);
    }
    plain = plain.replace(/^[\s:.-]+/, '').trim();

    // Deterministic translation, code-first (GOLDEN RULE):
    //  1) a provider/tax-authority CODE is language-independent → always the most
    //     specific title, so it wins even over a Microsoft internal catalog title.
    //  2) only when there is NO provider code do we try the PHRASE catalog for
    //     code-less localized reasons (e.g. GoSocket "Documento duplicado").
    //  3) otherwise fall back to the Microsoft catalog title, then the plain text.
    const providerCode = extractProviderCode(rawMessage);
    const catalogTitle = ErrorCodeandSubErrorCodeFriendlyNameMapping[`${errorCode}-${subErrorCode}`];
    const providerEnglish = providerCode ? ProviderErrorCatalog[providerCode] : undefined;
    const phraseEnglish = providerCode
        ? '' // a code is present: never phrase-guess off the surrounding text
        : (translatePhrase(additionalDetails) || translatePhrase(plain));
    const bestTranslation = providerEnglish || phraseEnglish;
    const translationSource: TranslationSource = providerEnglish ? 'code' : (phraseEnglish ? 'phrase' : '');
    // A deterministic code/phrase translation is guaranteed English regardless of the
    // provider message language, so it becomes the title and lets the UI hide the
    // redundant localized Details box.
    const title = bestTranslation || catalogTitle || plain || rawMessage || 'Validation error';
    const translated = Boolean(bestTranslation);

    // Location the error was reported at: prefer the "at [ ... ]" xpath, else the first
    // bracketed token that looks like an xpath.
    let location = '';
    const atMatch = rawMessage.match(/\bat\s*\[([^\]]*)\]/i);
    if (atMatch) {
        location = atMatch[1].trim();
    } else {
        const xpathMatch = rawMessage.match(/\[([^\]]*\/[^\]]*)\]/);
        if (xpathMatch) location = xpathMatch[1].trim();
    }

    return {
        errorCode,
        subErrorCode,
        source,
        ruleCode,
        providerCode,
        translated,
        translationSource,
        title,
        plainMessage: plain,
        businessRule: getRuleExplanation(ruleCode, businessRule),
        additionalDetails,
        location,
        rawMessage,
    };
}

// Returns parsed errors if the content is an EventErrors payload, otherwise null.
export function parseEventErrors(content: string): ParsedError[] | null {
    if (!content || content.indexOf('EventErrors') === -1) return null;
    const start = content.indexOf('[');
    const end = content.lastIndexOf(']');
    if (start === -1 || end === -1 || end < start) return null;
    const jsonSlice = content.slice(start, end + 1);
    try {
        const arr = JSON.parse(jsonSlice);
        if (!Array.isArray(arr) || arr.length === 0) return null;
        return arr.map(parseSingle);
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Status-line visuals for NON-error events (Sent / Succeeded / Received / ...).
// These events carry a short structured payload such as
//   "Supplier - GosocketCreation SkipSendingReason - Status - Sent"
// which is unreadable as raw text. We surface a colored status pill plus tidy
// key/value chips, mirroring the visual treatment of the failure cards.
// ---------------------------------------------------------------------------

export interface StatusPair { label: string; value: string; }

// Splits a "Label - Value Label - Value ..." payload into ordered pairs. A label is a
// word immediately followed by " - "; a value runs until the next label (may be empty).
export function parseStatusPairs(text: string): StatusPair[] {
    const pairs: StatusPair[] = [];
    const re = /([A-Za-z][A-Za-z0-9]*)\s-\s/g;
    const matches = Array.from(text.matchAll(re));
    for (let i = 0; i < matches.length; i++) {
        const label = matches[i][1];
        const valStart = (matches[i].index ?? 0) + matches[i][0].length;
        const valEnd = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
        pairs.push({ label, value: text.slice(valStart, valEnd).trim() });
    }
    return pairs;
}

export type BadgeColor = 'brand' | 'danger' | 'informative' | 'success' | 'warning' | 'subtle';

// Deterministic mapping of a normalized status keyword → pill color + friendly label.
// (The matching icon lives in FriendlyEventError.tsx so this module stays fluentui-free.)
export const StatusMeta: { [key: string]: { color: BadgeColor; label: string } } = {
    sent:                 { color: 'brand',       label: 'Sent' },
    delivered:            { color: 'success',     label: 'Delivered' },
    succeeded:            { color: 'success',     label: 'Succeeded' },
    success:              { color: 'success',     label: 'Succeeded' },
    completed:            { color: 'success',     label: 'Completed' },
    published:            { color: 'success',     label: 'Published' },
    accepted:             { color: 'success',     label: 'Accepted' },
    received:             { color: 'informative', label: 'Received' },
    synchronousresponse:  { color: 'informative', label: 'Sync response' },
    inprogress:           { color: 'informative', label: 'In progress' },
    processing:           { color: 'informative', label: 'Processing' },
    started:              { color: 'informative', label: 'Started' },
    pending:              { color: 'informative', label: 'Pending' },
    queued:               { color: 'informative', label: 'Queued' },
    batched:              { color: 'informative', label: 'Batched' },
    retrying:             { color: 'warning',     label: 'Retrying' },
    warning:              { color: 'warning',     label: 'Warning' },
    skipped:              { color: 'subtle',      label: 'Skipped' },
    failed:               { color: 'danger',      label: 'Failed' },
    error:                { color: 'danger',      label: 'Failed' },
    rejected:             { color: 'danger',      label: 'Rejected' },
};

const KNOWN_STATUS_WORDS = Object.keys(StatusMeta);

// Turns a PascalCase / camelCase label into spaced words ("ResponseType" → "Response type").
export function humanizeLabel(label: string): string {
    const spaced = label.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export interface EventStatus {
    text: string;            // encoding-fixed original
    pairs: StatusPair[];     // parsed key/value pairs (excludes the recognized status pair)
    statusKey: string;       // normalized KNOWN status keyword, '' if none/unknown
    statusLabel: string;     // friendly status label ('' when no known status)
    statusColor: BadgeColor; // pill color for the status ('subtle' when none)
    hasVisual: boolean;      // whether a pill or chips are available to render
}

// Parses a non-error event payload into a status keyword + key/value pairs.
//
// To avoid mistaking prose (e.g. "Error - supplier response - retry later") for a
// machine payload, the text is only treated as STRUCTURED when it either has an
// explicit "Status - <word>" pair, or has ≥2 pairs whose labels are all PascalCase/
// camelCase field tokens (the shape the connectors actually emit). Anything else is
// rendered as plain text. An unrecognized status value is shown as a neutral chip
// rather than a mis-colored pill.
export function parseEventStatus(content: string): EventStatus {
    const text = fixEncoding((content ?? '').trim());
    const empty: EventStatus = { text, pairs: [], statusKey: '', statusLabel: '', statusColor: 'subtle', hasVisual: false };
    if (!text) return empty;
    // Fold all whitespace (incl. newlines / non-breaking spaces) so the payload parses
    // regardless of how the backend line-wraps or joins the fields.
    const flat = text.replace(/\s+/g, ' ').trim();
    const startsWithLabel = /^[A-Za-z][A-Za-z0-9]*\s-\s/.test(flat);
    const candidatePairs = startsWithLabel ? parseStatusPairs(flat) : [];
    const statusPair = candidatePairs.find((p) => /^status$/i.test(p.label) && p.value);

    // Structured only when it's clearly the connector's machine format, not prose.
    const allLabelsFieldLike =
        candidatePairs.length > 0 && candidatePairs.every((p) => /^[A-Z][A-Za-z0-9]*$/.test(p.label));
    const structured = Boolean(statusPair) || (candidatePairs.length >= 2 && allLabelsFieldLike);
    const allPairs = structured ? candidatePairs : [];

    // The status keyword: prefer an explicit "Status - X" pair, else — only when the whole
    // payload IS a known status word (e.g. "Sent", "In Progress") — that word. Unknown
    // values do not become a colored pill.
    let statusKey = '';
    if (statusPair) {
        const norm = statusPair.value.toLowerCase().replace(/\s+/g, '');
        if (KNOWN_STATUS_WORDS.includes(norm)) statusKey = norm;
    } else if (allPairs.length === 0) {
        const whole = flat.toLowerCase().replace(/\s+/g, '');
        if (KNOWN_STATUS_WORDS.includes(whole)) statusKey = whole;
    }

    // Chips: keep every non-empty pair, but drop the status pair only when it was
    // promoted to a recognized pill (an unknown "Status - Foo" stays as a chip).
    const pairs = allPairs.filter((p) => {
        if (p.value.length === 0) return false;
        if (statusKey && /^status$/i.test(p.label)) return false;
        return true;
    });

    const meta = statusKey ? StatusMeta[statusKey] : undefined;
    const statusLabel = meta ? meta.label : '';
    const statusColor: BadgeColor = meta ? meta.color : 'subtle';
    const hasVisual = Boolean(statusKey) || pairs.length > 0;
    return { text, pairs, statusKey, statusLabel, statusColor, hasVisual };
}

// A provider (Pagero, GoSocket, EDICOM, ...) often reports the SAME underlying problem
// under several different codes (e.g. a specific "1300-72 Postal code is not valid" plus
// a generic "1400-4 Unable to complete request — Invalid recipient zip code"), so a group
// can carry several codes and detail lines while showing one primary title. No information
// is lost: every code, detail and raw message is preserved on the card.
export interface GroupedError {
    primaryTitle: string;
    codes: string[];         // internal codes, e.g. "1300-72", "1400-4"
    ruleCodes: string[];     // compliance rule codes, e.g. "BR-CO-14"
    providerCodes: string[]; // tax-authority / provider codes, e.g. "CFDI40147"
    detailsList: string[];   // distinct "Additional details" reasons
    businessRules: string[]; // distinct friendly rule explanations
    sources: string[];       // distinct "reported by" labels
    locations: string[];     // distinct XPath / field locations
    rawMessages: string[];   // every original message (for Technical details)
    translated: boolean;     // any member was resolved from a code/phrase catalog
    translationSource: TranslationSource; // 'code' preferred over 'phrase' for the note
    count: number;           // total occurrences merged into this group
}

// Controlled vocabulary of business "field concepts". Two errors that reference the
// same concept are treated as the same underlying problem and merged, even when the
// providers return different codes/wording. Deterministic (regex over a fixed list),
// never free-text similarity. Order matters: more specific concepts come first.
const FIELD_CONCEPTS: Array<[string, RegExp]> = [
    ['postalcode', /\b(zip\s*code|zipcode|postal\s*code|post\s*code|postcode|zip)\b/],
    ['taxamount', /\b(tax\s*(amount|total)|vat\s*(amount|total))\b/],
    ['taxid', /\b(tax\s*id|tax\s*identification|tax\s*registration|\brfc\b|\bgstin\b|\btin\b|\bsiret\b|\bsiren\b|\bnip\b)\b/],
    ['vat', /\bvat\b/],
    ['total', /\b(grand\s*total|document\s*total|invoice\s*total|total\s*amount|total\s*document)\b/],
    ['currency', /\bcurrenc(y|ies)\b/],
    ['iban', /\b(iban|bank\s*account)\b/],
    ['email', /\b(e-?mail|\bpec\b)\b/],
    ['date', /\bdate\b/],
    ['country', /\bcountry\b/],
    ['address', /\baddress\b/],
];

// Deterministic "kind of failure" so a MISSING field and an INVALID field for the same
// concept are not merged into one card (they need different fixes).
const KIND_PATTERNS: Array<[string, RegExp]> = [
    ['duplicate', /\bduplicat/],
    ['missing', /\b(missing|not provided|is required|are required|absent|no proporcion|no indic)/],
    ['mismatch', /\b(mismatch|does not match|do not match|inconsistent|not equal|no coincide)/],
    ['invalid', /\b(invalid|not valid|is not valid|incorrect|wrong|malformed|no es valido|no valido)/],
];

// Deterministic party so a BUYER-side and a SELLER-side error for the same concept are
// not merged into one card.
const PARTY_PATTERNS: Array<[string, RegExp]> = [
    ['buyer', /\b(buyer|recipient|receiver|customer|receptor|sold-?to)\b/],
    ['seller', /\b(seller|supplier|issuer|sender|emisor)\b/],
];

function firstMatch(patterns: Array<[string, RegExp]>, text: string): string {
    for (const [name, re] of patterns) {
        if (re.test(text)) return name;
    }
    return '';
}

// Deterministic "same underlying problem" signature from the recognised field concepts
// (plus failure-kind and party) in a message. Empty when no field concept is recognised —
// such errors fall back to exact-match grouping so we never merge things we don't understand.
function reasonSignature(err: ParsedError): string {
    const text = `${err.additionalDetails} ${err.title} ${err.plainMessage}`.toLowerCase();
    const found: string[] = [];
    for (const [concept, re] of FIELD_CONCEPTS) {
        if (re.test(text) && !found.includes(concept)) found.push(concept);
    }
    if (found.length === 0) return '';
    found.sort();
    const kind = firstMatch(KIND_PATTERNS, text);
    const party = firstMatch(PARTY_PATTERNS, text);
    return [found.join(','), kind, party].filter(Boolean).join('|');
}

const isGenericTitle = (t: string) => !t || /unable to complete request/i.test(t);
const codeLabel = (e: ParsedError) =>
    (e.errorCode || e.subErrorCode) ? `${e.errorCode}${e.subErrorCode ? `-${e.subErrorCode}` : ''}` : '';

const normalizeTitle = (t: string) => (t || '').toLowerCase().replace(/\s+/g, ' ').replace(/[.;:\s]+$/, '').trim();

// Two errors in the same signature bucket may only merge when their titles are
// "compatible": either the same specific title (a real duplicate), or one is a generic
// wrapper ("Unable to complete request") of the other. Two DIFFERENT specific titles that
// merely share a coarse concept (e.g. "Invalid issue date" vs "Invalid due date") are NOT
// the same problem and must stay on separate cards.
function titleCompatible(a: string, b: string): boolean {
    if (isGenericTitle(a) || isGenericTitle(b)) return true;
    return normalizeTitle(a) === normalizeTitle(b);
}

// 'code' beats 'phrase' when choosing the group's translation-source note.
function mergeTranslationSource(a: TranslationSource, b: TranslationSource): TranslationSource {
    if (a === 'code' || b === 'code') return 'code';
    if (a === 'phrase' || b === 'phrase') return 'phrase';
    return '';
}

function newGroup(err: ParsedError): GroupedError {
    const label = codeLabel(err);
    return {
        primaryTitle: err.title,
        codes: label ? [label] : [],
        ruleCodes: err.ruleCode ? [err.ruleCode] : [],
        providerCodes: err.providerCode ? [err.providerCode] : [],
        detailsList: err.additionalDetails ? [err.additionalDetails] : [],
        businessRules: err.businessRule ? [err.businessRule] : [],
        sources: err.source ? [err.source] : [],
        locations: err.location ? [err.location] : [],
        rawMessages: err.rawMessage ? [err.rawMessage] : [],
        translated: err.translated,
        translationSource: err.translationSource,
        count: 1,
    };
}

// Collapses errors that describe the same underlying problem into a single card.
// Errors sharing a recognised field-concept/kind/party signature merge (across different
// codes) ONLY when their titles are compatible; everything else falls back to exact-match
// dedupe. Tracks occurrence count, distinct codes, details and locations so nothing is hidden.
export function groupErrors(errors: ParsedError[]): GroupedError[] {
    const groups: GroupedError[] = [];
    // Each key maps to the (possibly several) groups that share that signature; a new error
    // joins the first title-compatible one, otherwise it starts its own group.
    const buckets = new Map<string, GroupedError[]>();
    const pushDistinct = (arr: string[], v: string) => { if (v && !arr.includes(v)) arr.push(v); };

    errors.forEach((err) => {
        const sig = reasonSignature(err);
        const key = sig
            ? `sig:${sig}`
            : `exact:${err.errorCode}|${err.subErrorCode}|${err.ruleCode}|${err.providerCode}|${err.title}|${err.businessRule}|${err.additionalDetails}`;
        const bucket = buckets.get(key) ?? [];
        const existing = bucket.find((g) => titleCompatible(g.primaryTitle, err.title));
        if (existing) {
            existing.count += 1;
            pushDistinct(existing.codes, codeLabel(err));
            pushDistinct(existing.ruleCodes, err.ruleCode);
            pushDistinct(existing.providerCodes, err.providerCode);
            pushDistinct(existing.detailsList, err.additionalDetails);
            pushDistinct(existing.businessRules, err.businessRule);
            pushDistinct(existing.sources, err.source);
            pushDistinct(existing.locations, err.location);
            pushDistinct(existing.rawMessages, err.rawMessage);
            existing.translated = existing.translated || err.translated;
            existing.translationSource = mergeTranslationSource(existing.translationSource, err.translationSource);
            // Prefer a specific title over a generic wrapper title.
            if (isGenericTitle(existing.primaryTitle) && !isGenericTitle(err.title)) {
                existing.primaryTitle = err.title;
            }
        } else {
            const group = newGroup(err);
            bucket.push(group);
            buckets.set(key, bucket);
            groups.push(group);
        }
    });
    return groups;
}

// Stable identity for a grouped error (for React keys / state), derived from its codes
// and title rather than array index so card state doesn't leak across re-renders.
export function groupKey(err: GroupedError): string {
    return [...err.codes, ...err.providerCodes, ...err.ruleCodes, err.primaryTitle]
        .filter(Boolean)
        .join('|');
}
